import forEach from 'lodash/forEach'
import groupBy from 'lodash/groupBy'
import head from 'lodash/head'
import indexOf from 'lodash/indexOf'
import join from 'lodash/join'
import map from 'lodash/map'
import omit from 'lodash/omit'
import reduce from 'lodash/reduce'
import size from 'lodash/size'
import snakeCase from 'lodash/snakeCase'
import split from 'lodash/split'
import uniq from 'lodash/uniq'
import values from 'lodash/values'
import getConfig from 'next/config'

import { Options, ProductSorting, Result } from '@Globals/Algolia/Types'
import { Group } from '@Globals/SplitTests/types'

const replicaSuffix = (popularityGroup: Group, sorting: ProductSorting) => {
  switch (sorting) {
    case ProductSorting.Default:
      switch (popularityGroup) {
        case 'b':
        case 'c':
        case 'd':
          return `_${popularityGroup}`
        default:
          return ''
      }
    default:
      return `_${sorting}`
  }
}

const generateIndex = ({ locale, popularityGroup, sorting }: Options) => {
  const { publicRuntimeConfig } = getConfig()
  return `products_${snakeCase(locale)}${publicRuntimeConfig.algoliaIndexSuffix}${replicaSuffix(popularityGroup, sorting)}`
}

const generateParams = (
  {
    boostMainObjectIDs,
    boostSubObjectIDs,
    buryObjectIDs,
    facetFilters,
    facets,
    filters,
    hitsPerPage,
    numericFilters,
    optionalFilters,
    page,
    pagination,
    query,
    sorting,
  }: Options,
  singleFacet?: string,
  isSSR = false,
) => {
  const searchParams = new URLSearchParams()
  if (!isSSR && !pagination) {
    searchParams.set('attributesToRetrieve', JSON.stringify(['objectID']))
  }

  if (singleFacet) {
    searchParams.set('facets', JSON.stringify([singleFacet]))
  } else if (facets.length > 0) {
    searchParams.set('facets', JSON.stringify(facets))
  }
  if (facetFilters?.length > 0) {
    searchParams.set('facetFilters', JSON.stringify(
      values(
        omit(
          groupBy(
            facetFilters,
            item => head(split(item, ':')),
          ),
          singleFacet || '',
        ),
      ),
    ))
  }

  let extendedFilters = filters
  let extendedOptionalFilters = optionalFilters

  // Main boosts are added twice to ensure they have higher priority than sub boosts
  if (boostMainObjectIDs?.length !== 0 && sorting === ProductSorting.Default) {
    extendedOptionalFilters = reduce(
      boostMainObjectIDs,
      (memo, item) => [...memo, `objectID:${item}`, `objectID:${item}`],
      extendedOptionalFilters,
    )
  }

  // Sub boosts are included regardless if they match the original query
  if (boostSubObjectIDs?.length !== 0) {
    extendedFilters = join([
      ...map(boostSubObjectIDs, item => `(objectID:${item})`),
      `(${extendedFilters})`,
    ], ' OR ')

    if (sorting === ProductSorting.Default) {
      extendedOptionalFilters = reduce(
        boostSubObjectIDs,
        (memo, item) => [...memo, `objectID:${item}`],
        extendedOptionalFilters,
      )
    }
  }

  // Buried objects should have a negative optional filter
  if (buryObjectIDs?.length !== 0 && sorting === ProductSorting.Default) {
    extendedOptionalFilters = [...extendedOptionalFilters, ...buryObjectIDs.map(id => `objectID:-${id}`)]
  }

  if (extendedFilters) {
    searchParams.set('filters', extendedFilters)
  }

  if (size(numericFilters) !== 0) {
    searchParams.set('numericFilters', JSON.stringify(
      reduce(numericFilters, (memo, { max, min }, key) => {
        if (min === max) {
          return [...memo, `${key} = ${min}`]
        }

        return [...memo, `${key} >= ${min}`, `${key} <= ${max}`]
      }, []),
    ))
  }

  if (extendedOptionalFilters?.length > 0) {
    searchParams.set('optionalFilters', JSON.stringify(extendedOptionalFilters))
  }
  searchParams.set('hitsPerPage', singleFacet ? '0' : (hitsPerPage || '1000'))

  if (pagination && page && page >= 1) {
    searchParams.set('page', (page - 1).toString())
  }

  if (query) {
    searchParams.set('query', query)
    searchParams.set('optionalWords', query)
  }

  return searchParams.toString()
}

export const generateURL = () => {
  const { publicRuntimeConfig } = getConfig()
  const url = new URL(`https://${publicRuntimeConfig.algoliaAppID.toLowerCase()}-dsn.algolia.net/1/indexes/*/queries`)
  url.searchParams.set('x-algolia-application-id', publicRuntimeConfig.algoliaAppID)
  url.searchParams.set('x-algolia-api-key', publicRuntimeConfig.algoliaAPIKey)
  return url.toString()
}

export const generateRequests = (options: Options, isSSR?: boolean) => {
  const indexName = generateIndex(options)

  return [
    {
      indexName,
      params: generateParams(options, undefined, isSSR),
    },
    ...map(
      uniq(map(options.facetFilters, item => head(split(item, ':')))),
      singleFacet => ({
        indexName,
        params: generateParams(options, singleFacet),
      }),
    ),
  ]
}

export const parseResults = (results: any, isSSR: boolean | undefined, options: Options): Result => {
  if (results && results.length > 0) {
    const objectIDs = map(results[0]?.hits, 'objectID')

    if (objectIDs && objectIDs.length !== 0 && options.sorting === ProductSorting.Default) {
      if (options.boostSpecificPositions && options.boostSpecificPositions.length !== 0) {
        forEach(options.boostSpecificPositions, ([objectID, position]) => {
          const to = position - 1
          if (to < 0 || to >= objectIDs!.length) {
            return
          }

          const from = indexOf(objectIDs, objectID)
          if (from === -1 || from <= to) {
            return
          }

          objectIDs!.splice(to, 0, objectIDs!.splice(from, 1)[0])
        })
      }
    }

    return {
      facets: reduce(
        results,
        (memo, item) => ({
          ...memo,
          ...item.facets || {},
        }),
        {},
      ),
      facetsStats: isSSR ? results[0]?.facets_stats || {} : {},
      hits: (isSSR || options.pagination) ? results[0]?.hits || [] : [],
      objectIDs,
      page: results[0]?.page || 0,
      pages: results[0]?.nbPages || 0,
      totalHits: results[0]?.nbHits || 0,
    }
  }

  return {
    facets: {},
    facetsStats: {},
    hits: [],
    objectIDs: [],
    page: 0,
    pages: 0,
    totalHits: 0,
  }
}

export const algoliaFetcher = async (options: Options, isSSR?: boolean): Promise<Result> => {
  const url = generateURL()
  const fetchedData = await fetch(url, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json; charset=UTF-8',
    }),
    body: JSON.stringify({
      requests: generateRequests(options, isSSR),
    }),
  })

  if (fetchedData.status !== 200) {
    throw new Error('Failed to fetch from Algolia')
  }

  try {
    const data = await fetchedData.json()
    return parseResults(data?.results, isSSR, options)
  } catch (e) {
    throw new Error('Something went wrong trying to get data from Algolia')
  }
}
